import {
  type PropsWithChildren,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import {
  type SaveShortcutPriority,
  type SaveShortcutStrategy,
  SaveShortcutBoundaryContext,
  SaveShortcutRegistryContext,
} from './context';

type SaveShortcutBoundaryProps = PropsWithChildren<{
  active?: boolean;
  priority: SaveShortcutPriority;
  strategy?: SaveShortcutStrategy;
}>;

function SaveShortcutBoundary({
  active = true,
  children,
  priority,
  strategy = 'active',
}: SaveShortcutBoundaryProps) {
  const registry = useContext(SaveShortcutRegistryContext);
  const [id] = useState(() => Symbol('save-shortcut-boundary'));

  useLayoutEffect(() => {
    if (!registry) {
      return;
    }

    return registry.registerBoundary({ active, id, priority, strategy });
  }, [active, id, priority, registry, strategy]);

  const contextValue = useMemo(() => ({ id, priority }), [id, priority]);

  return (
    <SaveShortcutBoundaryContext.Provider value={contextValue}>
      {children}
    </SaveShortcutBoundaryContext.Provider>
  );
}

export default SaveShortcutBoundary;
